import {nextTick} from "vue";
import {useRouter} from "vue-router";
import getModal from "@/functions/getModal";
import errorIcon from '@/assets/img/icons/alert-octagon.svg'
import warningIcon from '@/assets/img/icons/alert-triangle.svg'
import infoIcon from '@/assets/img/icons/info-circle.svg'
import successIcon from '@/assets/img/icons/check-circle.svg'
import {mapErrorMessages} from '@/functions/errorMessages'
import {appendSanitizedHtml} from '@/functions/sanitizeHtml'
import {isSignInRedirectAlert} from '@/functions/authenticatedRequest'

const {showModal} = getModal()

const getToastAlert = () => {
	const router = useRouter()
	const alert429 = [{
		'type': 'ERROR',
		'title': '',
		'message': 'You\'ve made too many requests in a short period. Please wait a minute before trying again.'
	}]

	const displayDefaultError = async () => {
		router.push('/').then(() => displayToastAlert())
	}

	const display429Error = () => {
		displayToastAlert(alert429)
	}

	const displayToastAlert = async (alerts = null, isFromApp = false) => {
		if (!alerts && isSignInRedirectAlert()) return
		hideToastAlert()
		let btnId = 1
		let toastId = 1

		if (alerts) {
			if (alerts.constructor !== Array) {
				alerts = [alerts]
			}
			if (isFromApp) {
				alerts = mapErrorMessages(alerts)
			}
		}
		else {
			alerts = [{
				'message': process.env.VUE_APP_DEFAULT_ERROR_MESSAGE
			}]
		}

		for (const alert of alerts) {
			if (alert.title || alert.message) {
				const icon = document.createElement('img')
				icon.classList.add('toast-alert-icon')
				icon.alt = ''

				const newToastAlert = document.createElement('div')
				newToastAlert.classList.add('alert-message')
				newToastAlert.setAttribute('role', alert.type === 'INFO' || alert.type === 'SUCCESS' ? 'status' : 'alert')
				const newToastAlertInnerDiv = document.createElement('div')
				newToastAlertInnerDiv.classList.add('alert-message-inner-container')
				const newToastContent = document.createElement('div')
				newToastContent.classList.add('toast-alert-content')
				newToastContent.id = 'toastAlertContent' + toastId
				toastId += 1

				if (alert.isRedirectToLogin) {
					const redirectUri = sessionStorage.getItem('redirect_uri')
					const target =
						redirectUri === 'preparer' ? '/preparer/login' :
						redirectUri === 'preparer_link' ? '/preparer/link-client-account' :
						'/'
					await router.replace(target)
					await nextTick()
				}

				if (!alert.type) {
					alert.type = 'ERROR'
				}

				if (alert.type === 'INFO') {
					icon.src = infoIcon
					icon.classList.add('filter-primary-500')
				}
				else if (alert.type === 'WARNING') {
					icon.src = warningIcon
					icon.classList.add('filter-warning-500')
				}
				else if (alert.type === 'SUCCESS') {
					icon.src = successIcon
					icon.classList.add('filter-success-500')
				}
				else {
					icon.src = errorIcon
					icon.classList.add('filter-error-500')
				}

				let titleTag
				let messageTag

				if (alert.title) {
					titleTag = document.createElement('span')
					appendSanitizedHtml(titleTag, alert.title)
					titleTag.classList.add('d-block', 'text-md', 'mb-2')
					if (alert.message) {
						titleTag.classList.add('fw-bold')
					}
					newToastContent.appendChild(titleTag)
				}

				if (alert.message) {
					messageTag = document.createElement('span')
					messageTag.classList.add('text-md')
					appendSanitizedHtml(messageTag, alert.message)

					newToastContent.appendChild(messageTag)
				}

				if (alert.faq) {
					if (alert.faqText) {
						const faqTextButton = document.createElement('button')
						faqTextButton.type = "button"
						faqTextButton.onclick = function () {showModal('faq' + alert.faq)}
						faqTextButton.innerText = alert.faqText
						newToastContent.appendChild(faqTextButton)
					}
					else {
						const faqIcon = document.createElement('button')
						faqIcon.classList.add('help-a')
						faqIcon.type = "button"
						faqIcon.onclick = function () {showModal('faq' + alert.faq)}
						const srSpan = document.createElement('span')
						srSpan.classList.add('visually-hidden')
						srSpan.textContent = 'click to open faq'
						faqIcon.appendChild(srSpan)

						if (alert.message) {
							messageTag.appendChild(faqIcon)
						}
						else {
							titleTag.appendChild(faqIcon)
						}
					}
				}

				newToastAlertInnerDiv.appendChild(newToastContent)

				newToastAlert.classList.add(alert.type.toLowerCase() + '-message')

				if (alert.field && document.getElementById(alert.field)) {
					const newToastAlertFixItBtn = document.createElement('button')
					newToastAlertFixItBtn.id = 'fixItButton' + btnId
					newToastAlertFixItBtn.classList.add('toast-alert-btn')
					newToastAlertFixItBtn.tabIndex = 1
					newToastAlertFixItBtn.textContent = 'Fix this'
					btnId += 1
					newToastAlertFixItBtn.onclick = function () {
						focusOnFieldError(alert.field)
					}
					document.getElementById(alert.field).classList.add(alert.field.toLowerCase() + '-message')
					document.getElementById(alert.field).classList.remove('info-message')
					document.getElementById(alert.field).setAttribute('aria-invalid', 'true')
					document.getElementById(alert.field).setAttribute('aria-describedby', newToastContent.id)
					document.getElementById(alert.field).dataset.toastField = 'true'
					newToastAlert.dataset.field = alert.field

					if (alert.type && alert.type !== '429') {
						newToastAlertInnerDiv.appendChild(newToastAlertFixItBtn)
					}
				}

				newToastAlert.appendChild(icon)
				newToastAlert.appendChild(newToastAlertInnerDiv)
				const toastAlertAreaDiv = document.getElementById('toastAlertArea')
				toastAlertAreaDiv.insertBefore(newToastAlert, null)
			}
		}

		if (document.getElementById('fixItButton1')) {
			document.getElementById('fixItButton1').focus()
		}
		scrollTo(0,0)
	}

	const focusOnFieldError = (id) => {
		let fieldError
		let fieldError2
		if (id === 'confirmEmail') {
			fieldError = document.getElementById('email')
			fieldError2 = document.getElementById('confirmEmail')
			fieldError.classList.add('error-message')
			fieldError2.classList.add('error-message')
			fieldError.classList.add('error-message-shading')
			fieldError2.classList.add('error-message-shading')
			fieldError.focus()
			fieldError.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start'})
			setTimeout(()=>{
				fieldError.classList.remove('error-message-shading')
				fieldError2.classList.remove('error-message-shading')
			}, 2000)
		}
		else {
			fieldError = document.getElementById(id)
			fieldError.classList.add('error-message')
			fieldError.classList.add('error-message-shading')
			fieldError.focus()
			fieldError.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start'})
			setTimeout(()=>{fieldError.classList.remove('error-message-shading')}, 2000)
		}
	}

	const hideToastAlert = () => {
		const toastArea = document.getElementById('toastAlertArea');
		if (!toastArea) return;
		toastArea.querySelectorAll('.alert-message').forEach(alertElement => {
			alertElement.remove();
		});
		document.querySelectorAll('[data-toast-field]').forEach(field => {
			field.removeAttribute('aria-invalid')
			field.removeAttribute('aria-describedby')
			delete field.dataset.toastField
		})
	}

	return {
		displayDefaultError,
		displayToastAlert,
		display429Error,
		hideToastAlert
	}
}

export default getToastAlert
